/**
 * Configuration API Multi-Région (4 RÉGIONS)
 *
 * Détecte la région et construit les URLs API appropriées.
 *
 * Vague 8 (11/10/2026) : un seul schema d'URL, <region>.<domaine> (bouake.conquete.app en PROD,
 * bouake.dev.conquete.app en DEV). La region se lit d'abord dans l'HOTE ; le prefixe de chemin
 * (/bouake/...) n'est qu'un repli pour l'acces local (http://localhost:5090/bouake/...).
 *
 * Régions supportées:
 * - BOUAKE (Gbêkê)
 * - ALEPE (La Mé)
 * - BOUNDIALI (Bagoué)
 * - ABOBO (Abidjan)
 */

export interface RegionInfo {
  code: string;
  label: string;
  database: string;
}

// Configuration des 4 régions disponibles
export const REGIONS: Record<string, RegionInfo> = {
  bouake: {
    code: 'BOUAKE',
    label: 'Bouaké (Gbêkê)',
    database: 'conquete_bouake_2025_dev'
  },
  alepe: {
    code: 'ALEPE',
    label: 'Alépé (La Mé)',
    database: 'conquete_alepe_2025_dev'
  },
  boundiali: {
    code: 'BOUNDIALI',
    label: 'Boundiali (Bagoué)',
    database: 'conquete_boundiali_2025_dev'
  },
  abobo: {
    code: 'ABOBO',
    label: 'Abobo (Abidjan)',
    database: 'conquete_abobo_2025_dev'
  }
};

// Pattern regex pour détecter les régions dans le chemin (/bouake, /bouake/..., jamais /bouakex)
const REGION_PATTERN = /^\/(bouake|alepe|boundiali|abobo)(?=\/|$)/;

/** Region portee par le sous-domaine : bouake.conquete.app -> 'bouake' ; sinon null */
export const getRegionFromHost = (): string | null => {
  const labels = window.location.hostname.toLowerCase().split('.');
  if (labels.length < 2) return null;
  return REGIONS[labels[0]] ? labels[0] : null;
};

/** Region portee par le prefixe de chemin : /bouake/... -> 'bouake' ; sinon null */
export const getRegionFromPath = (): string | null => {
  const match = window.location.pathname.match(REGION_PATTERN);
  return match ? match[1] : null;
};

/** Cle de la region courante : sous-domaine d'abord, chemin en repli */
export const getRegionKey = (): string | null => getRegionFromHost() || getRegionFromPath();

/**
 * Prefixe a placer devant les chemins (API, modules). Il suit le chemin affiche par le navigateur :
 * '/bouake' si l'URL en porte un (acces local), '' sinon (sous-domaine : le gateway lit l'hote).
 * Ne fabrique JAMAIS un prefixe a partir de l'hote.
 */
export const getRegionPrefix = (): string => {
  const fromPath = getRegionFromPath();
  return fromPath ? `/${fromPath}` : '';
};

// Récupère les infos de la région actuelle
export const getCurrentRegion = (): RegionInfo | null => {
  const key = getRegionKey();
  return key ? REGIONS[key] || null : null;
};

// Retourne le code de région pour les headers API
export const getRegionCode = (): string | null => {
  const region = getCurrentRegion();
  return region?.code || null;
};

// Retourne le nom de la base de données de la région
export const getRegionDatabase = (): string | null => {
  const region = getCurrentRegion();
  return region?.database || null;
};

// Construit l'URL API (relative) avec le préfixe de région s'il est dans le chemin
export const getApiUrl = (endpoint: string): string => {
  const regionPrefix = getRegionPrefix();

  // Si l'endpoint commence déjà par /api, ajouter le préfixe de région devant
  if (endpoint.startsWith('/api')) {
    return `${regionPrefix}${endpoint}`;
  }

  // Si l'endpoint ne commence pas par /, l'ajouter
  if (!endpoint.startsWith('/')) {
    endpoint = '/' + endpoint;
  }

  return `${regionPrefix}/api${endpoint}`;
};

// Retourne les headers à ajouter aux requêtes API pour identifier la région
export const getRegionHeaders = (): Record<string, string> => {
  const region = getCurrentRegion();

  if (!region) {
    return {};
  }

  return {
    'X-Region': region.code,
    'X-Region-DB': region.database
  };
};

// Vérifie si une région est sélectionnée
export const hasRegion = (): boolean => {
  return getCurrentRegion() !== null;
};

// Helper pour les fetch avec région
export const fetchApi = async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
  const url = getApiUrl(endpoint);
  const headers = {
    ...getRegionHeaders(),
    ...(options.headers || {})
  };
  return fetch(url, { ...options, headers });
};

export default {
  REGIONS,
  getRegionFromHost,
  getRegionFromPath,
  getRegionKey,
  getRegionPrefix,
  getApiUrl,
  getCurrentRegion,
  getRegionCode,
  getRegionDatabase,
  getRegionHeaders,
  hasRegion,
  fetchApi
};
